import { ChatEligibleUser, ChatEligibleUserRole, ChatSummary } from '../models/Chat';
import type { OrganizationType } from '../models/Organization';
import { chatApiClient } from '../chatApiClient';
import {
  chatParticipants,
  isOrganizationScopedChat,
  isSupportCaseChat,
  type ChatInbox,
} from './chat';

export interface ChatPartnerOption {
  userId: string;
  name: string | null;
  profileImageUrl?: string | null;
  /** Display context only — do not use for eligibility. */
  organizationType?: OrganizationType | null;
  roles?: ChatEligibleUserRole[] | null;
  tutorSpaceName?: string | null;
  hostOrganizationName?: string | null;
  userBandName?: string | null;
}

export function findChatWithPair(
  chats: ChatSummary[],
  userIdA: string,
  userIdB: string
): ChatSummary | undefined {
  return chats.find((chat) => {
    // Org staff/support chats and support cases are not 1:1 pair matches.
    if (isOrganizationScopedChat(chat) || isSupportCaseChat(chat)) return false;
    const members = chatParticipants(chat);
    if (members.length !== 2) return false;
    const ids = new Set(members.map((member) => member.userId));
    return ids.has(userIdA) && ids.has(userIdB);
  });
}

export function chatPairExists(
  chats: ChatSummary[],
  userIdA: string,
  userIdB: string
): boolean {
  return findChatWithPair(chats, userIdA, userIdB) != null;
}

/** List chats and reuse an existing pair, or create one. Returns the chat id. */
export async function openOrCreateChatWith(
  currentUserId: string,
  partnerUserId: string,
  inbox: ChatInbox = 'user'
): Promise<string> {
  const chats = await chatApiClient.listChats(inbox);
  const existing = findChatWithPair(chats, currentUserId, partnerUserId);
  if (existing?.id) {
    return existing.id;
  }

  const created = await chatApiClient.createChat({
    participantOneUserId: currentUserId,
    participantTwoUserId: partnerUserId,
  });
  if (!created.id) {
    throw new Error('Chat create returned no id');
  }
  return created.id;
}

export function filterPartnersWithoutExistingChat(
  options: ChatPartnerOption[],
  chats: ChatSummary[],
  currentUserId: string
): ChatPartnerOption[] {
  return options.filter((option) => !chatPairExists(chats, currentUserId, option.userId));
}

export function mapEligibleUsersToPartnerOptions(
  users: ChatEligibleUser[]
): ChatPartnerOption[] {
  return users.map((user) => ({
    userId: user.id,
    name: user.name,
    profileImageUrl: user.profileImageUrl,
    organizationType: user.organizationType ?? null,
    roles: user.roles ?? null,
    tutorSpaceName: user.tutorSpaceName ?? null,
    hostOrganizationName: user.hostOrganizationName ?? null,
    userBandName: user.userBandName ?? null,
  }));
}

export async function loadEligibleChatPartnerOptions(
  chats: ChatSummary[],
  currentUserId: string
): Promise<ChatPartnerOption[]> {
  const eligibleUsers = await chatApiClient.listEligibleUsers();
  const partners = mapEligibleUsersToPartnerOptions(eligibleUsers);
  return filterPartnersWithoutExistingChat(partners, chats, currentUserId);
}
